<template>
  <div>
    <AtomicUiNotification v-if="errorState" class="cell small-12" severity="error" :closable="false">
      {{ errorMessages.productDetailsDrawer }} <br />
      <AtomicUiButton class="link-body" variant="link" @click.stop.prevent="getQuickViewProductDetails"> Refresh </AtomicUiButton>
    </AtomicUiNotification>
    <div v-else-if="productDetail">
      <ProductThumbnailWrapper
        :key="productDetail.id"
        :product="transformedProduct"
        alignment="vertical"
        :image-width="422"
        :show-wish-list-btn-on-image="false"
        :disable-color="(hasPurchasedItems && disableFields.color_size) || (!hasPurchasedItems && hasPurchasedDropdownChanged)"
        :is-large-card="true"
      />
      <AtomicUiNotification v-if="hasPurchasedItems && isRegistrant" :closable="false" severity="info" class="margin-bottom-xl">
        This gift has been {{ !itemFullyPurchased ? 'partially' : '' }} purchased and cannot be edited. To add it to your registry in a different size
        or color, visit the product page.
        <a :href="productDetail?.identifier?.productUrl" target="_blank">Add Now</a>
      </AtomicUiNotification>
      <div v-if="!isRegistrant" class="margin-top-m margin-bottom-m">
        <!-- Adding quantity -->
        <p class="label">{{ quantityPurchased }} of {{ quantityRequested }} PURCHASED</p>
        <div class="select-container grid-x">
          <select
            id="itemQty"
            v-model="itemQty"
            name="dropdown"
            class="selected small-4"
            :disabled="disablePurchaseQtyDD || enableQuantityDropdown || !isAvailable"
            @change="updateItemQty"
          >
            <option v-for="(quantity, index) in remainingQuantity" :key="index" :value="quantity">
              {{ quantity }}
            </option>
          </select>
          <label for="itemQty" class="select-label-top selected-label-top">Qty</label>
        </div>
      </div>
      <div v-if="isRegistrant">
        <div class="margin-top-m margin-bottom-m grid-x">
          <div class="small-4 margin-right-s">
            <p class="medium">Requested</p>
            <div class="select-container">
              <select
                id="requestedQuantity"
                v-model="requestedQuantity"
                name="dropdown"
                class="selected small-4"
                :disabled="(hasPurchasedItems && disableFields.requested) || isRegistryPostCompletion"
                @change="updateRegistrantRequestQty"
              >
                <option v-for="(quantity, index) in requestQuantityDropdownValue" :key="index" :value="quantity">
                  {{ quantity }}
                </option>
              </select>
              <label for="requestedQuantity" class="select-label-top selected-label-top">Qty</label>
            </div>
          </div>
          <div class="small-4">
            <p class="medium">Purchased</p>
            <div class="select-container">
              <select
                id="purchasedQuantity"
                v-model="purchasedQuantity"
                name="dropdown"
                class="selected small-4"
                :disabled="(hasPurchasedItems && disableFields.purchased) || (!hasPurchasedItems && isColorOrSizeChanged)"
                @change="updateRegistrantPurchasedQty"
              >
                <option v-for="(quantity, index) in remainQuantity" :key="index" :value="quantity">
                  {{ quantity }}
                </option>
              </select>
              <label for="purchasedQuantity" class="select-label-top selected-label-top">Qty</label>
            </div>
          </div>
        </div>
        <div class="large-margin-top-m small-margin-bottom-s mobile-cta-s">
          <div class="grid-margin-x grid-margin-y">
            <div class="cell small-12">
              <AtomicUiButton :disabled="itemFullyPurchased" variant="secondary" :expanded="true" @click.stop="toggleDeleteModalOverlay(product)">
                Remove Item
              </AtomicUiButton>
            </div>
            <div class="cell small-12">
              <AtomicUiButton variant="secondary" :expanded="true" :disabled="disableAddToBagCta" @click="handleAddToBag">
                {{ disabledButtonText ? disabledButtonText : 'Add To Bag' }}
              </AtomicUiButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <AtomicUiSkeleton />
    </div>
  </div>
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { productTransformer } from '~/utils/interface';
import { errorMessages } from 'config';
import { useQuickViewStore } from '~/stores/quickView';
import { useRegistrantMyGiftTabStore } from '~/stores/registrantMyGiftTab';
import { useEnvPropsStore } from '~/stores/envProps';
import ProductThumbnailWrapper from '~/common/ProductThumbnailWrapper';
import { useQuickView } from '~/composables/useQuickView';
import { FETCH_QUICK_VIEW_PRODUCT_DETAILS } from '~/types/actions';
import {
  SET_REGISTRANT_REQUESTED_QTY,
  SET_REGISTRANT_PURCHASED_QTY,
  SET_QUICK_VIEW_PRODUCT_DETAILS,
  SET_ITEM_QTY,
  UPDATE_PREFERENCE_STATUS,
  SET_STORE_ERROR_STATE,
} from '~/types/mutations';

const props = defineProps({
  product: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  productId: {
    type: Number,
    required: false,
    default: null,
  },
  upcId: {
    type: Number,
    required: false,
    default: null,
  },
  isRegistrant: {
    type: Boolean,
    required: false,
    default: false,
  },
  quantityRequested: {
    type: Number,
    required: false,
    default: null,
  },
  quantityPurchased: {
    type: Number,
    required: false,
    default: 0,
  },
  quantityReceived: {
    type: Number,
    required: false,
    default: 0,
  },
});

const emits = defineEmits(['toggleDeleteModalOverlay']);

const quickViewStore = useQuickViewStore();
const registrantMyGiftTabStore = useRegistrantMyGiftTabStore();
const envPropsStore = useEnvPropsStore();

const purchasedQuantity = ref(props.quantityPurchased);
const requestedQuantity = ref(props.quantityRequested);
const requestQuantityDropdownValue = ref(props.quantityRequested + 50);
const itemQty = ref(1);
const transformedProduct = ref('');

const {
  disabledButtonText,
  disablePurchaseQtyDD,
  disableAddToBagCta,
  productDetail,
  productDetailsApiErrorResponse,
  isAvailable,
  hasPurchasedItems,
  itemFullyPurchased,
  hasPurchasedDropdownChanged,
  isColorOrSizeChanged,
  disableFields,
  enableQuantityDropdown,
  errorState,
  handleAddToBag,
  toggleDeleteModalOverlay,
  productDetailMeta,
  productImage,
} = useQuickView({
  propQuantityRequested: props.quantityRequested,
  propQuantityPurchased: props.quantityPurchased,
  propIsRegistrant: props.isRegistrant,
  propProduct: props.product,
  emits,
});

const registrantResponse = computed(() => registrantMyGiftTabStore.registrantMyGiftResponse);
const registryHeaderData = computed(() => registrantResponse.value?.registryHeaderData);
const isRegistryPostCompletion = computed(() => {
  if (props.isRegistrant && registryHeaderData.value?.registryStatus.toUpperCase() === 'POST COMPLETION') return true;
  return false;
});

const getQuickViewProductDetails = async () => {
  if (props.isRegistrant) {
    quickViewStore[SET_REGISTRANT_REQUESTED_QTY](requestedQuantity.value);
    quickViewStore[SET_REGISTRANT_PURCHASED_QTY](props.quantityPurchased);
    quickViewStore[UPDATE_PREFERENCE_STATUS](true);
  }
  await quickViewStore[FETCH_QUICK_VIEW_PRODUCT_DETAILS](Object.assign(envPropsStore.envProps.headerContext, { productId: props.productId }));

  const productNumofImages = [productDetailMeta.value?.product?.[0]?.detail?.additionalImagesCount ?? ''];
  const productNumofVideos =
    productDetailMeta.value?.product?.[0]?.detail?.isVideoImageRailEnabled && productDetailMeta.value.product?.[0]?.imagery?.videoId ? ['1'] : ['0'];
  const data = productDetailMeta.value?.meta?.analytics?.data;
  fireTag('view', {
    page_type: 'registry',
    page_name: 'registry product details',
    product_availability_message: data?.product_availability_message || '',
    product_brand: data?.product_brand || '',
    product_id: data?.product_id || '',
    product_low_availability: data?.product_low_availability || '',
    product_name: data?.product_name || '',
    product_numofimages: productNumofImages,
    product_numofvideos: productNumofVideos,
    product_original_price: data?.product_original_price || '',
    product_price: data?.product_price || '',
    product_pricing_state: data?.product_pricing_state || '',
    product_pwp_available: data?.product_pwp_available || '',
    product_quick_checkout: productDetailMeta.value?.uiKillSwitches?.isQuickBuyEnabled || 'false',
    product_rating: data?.product_rating,
    product_reviews: data?.product_reviews,
    product_type: data?.product_type,
    product_video: data?.product_video,
  });

  if (productDetailsApiErrorResponse.value) {
    fireTag('link', {
      event_name: 'Registry error',
      error_category: 'registry',
      error_code: [`REG${productDetailsApiErrorResponse.value?.response?.data?.errors?.[0]?.code || '999'}`],
      error_label: [' '],
      error_message: [errorMessages.serverProcessingError],
      error_misc: [' '],
      error_scope: ['Global'],
    });
    return;
  }
  transformedProduct.value = productTransformer({
    product: productDetail.value,
    selectedUpcId: props.upcId,
    hasPurchasedItems: hasPurchasedItems.value,
    isRegistrant: props.isRegistrant,
    productCardImage: productImage.value,
    isRegistryPostCompletion: isRegistryPostCompletion.value,
  });
  if (!transformedProduct.value) {
    quickViewStore[SET_STORE_ERROR_STATE](true);
    fireTag('link', {
      event_name: 'Registry error',
      error_category: 'registry',
      error_code: [`REG${productDetailsApiErrorResponse.value?.response?.data?.errors?.[0]?.code || '999'}`],
      error_label: [' '],
      error_message: [errorMessages.serverProcessingError],
      error_misc: [' '],
      error_scope: ['Global'],
    });
  } else {
    quickViewStore[SET_QUICK_VIEW_PRODUCT_DETAILS](transformedProduct.value);
  }
};

const remainQuantity = computed(() => {
  if (!props.isRegistrant && props.quantityPurchased >= props.quantityRequested) {
    return 0;
  }
  // adding max quantity and created array value
  return Array(51)
    .fill(props.quantityPurchased)
    .map((e, i) => e + i);
});

const remainingQuantity = computed(() => {
  const maxAddToBag = productDetail.value?.detail?.maxQuantity;
  const maxOpenToBuy = props.quantityRequested - props.quantityPurchased;
  let allowAddToBag = maxAddToBag && maxAddToBag < maxOpenToBuy ? maxAddToBag : maxOpenToBuy;
  if (allowAddToBag <= 0) {
    allowAddToBag = 1;
  }
  return allowAddToBag;
});

const updateItemQty = () => {
  quickViewStore[SET_ITEM_QTY](itemQty.value);
};
const updateRegistrantRequestQty = () => {
  quickViewStore[SET_REGISTRANT_REQUESTED_QTY](requestedQuantity.value);
};
const updateRegistrantPurchasedQty = () => {
  quickViewStore[SET_REGISTRANT_PURCHASED_QTY](purchasedQuantity.value);
};

getQuickViewProductDetails();

watch(
  () => props.productId,
  () => {
    getQuickViewProductDetails();
  },
);
</script>

<style scoped lang="scss">
.mobile-cta-s {
  display: none;

  @include breakpoint(medium down) {
    display: block;
  }
}

.slideout-body {
  .product-thumbnail-wrapper {
    height: unset;
  }
}
</style>
